import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { reconciliationRepositoryToken } from './repository';
import { safePageError } from './ReconciliationPage';
import type { ReconciliationExceptionDetail } from './types';
import {
  composeResolutionReason,
  formatExactMoney,
  reconciliationEvidence,
  RESOLUTION_REASON_MAX_LENGTH,
} from './types';

export function ReconciliationExceptionDetailPage({
  dataSource,
}: {
  readonly dataSource: DataSource;
}) {
  const { exceptionId = '' } = useParams();
  const repository = useRepository(reconciliationRepositoryToken);
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refresh, setRefresh] = useState(0);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [acknowledgeOpen, setAcknowledgeOpen] = useState(false);
  const resource = useResource(
    (signal) => repository.getException(exceptionId, signal),
    [repository, exceptionId, refresh],
    () => false,
  );
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={safePageError(resource.error, t('reconciliation.errors.loadException'))}
        onRetry={() => setRefresh((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              actions={
                resource.data.status === 'OPEN' ? (
                  <Stack direction="row" spacing={1}>
                    {resource.data.type === 'WALLET_LEDGER_CHAIN_MISMATCH' ? (
                      <Button onClick={() => setAcknowledgeOpen(true)} variant="outlined">
                        {t('reconciliation.actions.acknowledgeException')}
                      </Button>
                    ) : null}
                    <Button onClick={() => setResolveOpen(true)} variant="contained">
                      {t('reconciliation.actions.resolveException')}
                    </Button>
                  </Stack>
                ) : undefined
              }
              backTo={`${paths.reconciliation}?tab=exceptions`}
              dataSource={dataSource}
              subtitle={`${resource.data.severity} · ${resource.data.status}`}
              title={resource.data.id}
            />
            <Overview exception={resource.data} language={language} />
            <MismatchEvidence exception={resource.data} />
            <RelatedRecords exception={resource.data} />
            <Resolution exception={resource.data} language={language} />
            <ResolveDialog
              exception={resource.data}
              onClose={() => setResolveOpen(false)}
              onResolved={() => {
                setResolveOpen(false);
                setRefresh((value) => value + 1);
              }}
              open={resolveOpen}
            />
            <AcknowledgeDialog
              exception={resource.data}
              onAcknowledged={() => {
                setAcknowledgeOpen(false);
                setRefresh((value) => value + 1);
              }}
              onClose={() => setAcknowledgeOpen(false)}
              open={acknowledgeOpen}
            />
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

function Overview({
  exception,
  language,
}: {
  readonly exception: ReconciliationExceptionDetail;
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  return (
    <Section title={t('reconciliation.sections.exceptionSummary')}>
      <MetadataList
        items={[
          { label: t('reconciliation.fields.type'), value: exception.type },
          {
            label: t('reconciliation.fields.entity'),
            value: `${exception.entityType} · ${exception.entityId}`,
          },
          {
            label: t('reconciliation.fields.partner'),
            value:
              exception.partner === null
                ? t('common.notAvailable')
                : `${exception.partner.code} · ${exception.partner.name}`,
          },
          { label: t('reconciliation.fields.occurrences'), value: exception.occurrenceCount },
          timeItem(t('reconciliation.fields.firstDetected'), exception.firstDetectedAt, language),
          timeItem(t('reconciliation.fields.lastDetected'), exception.lastDetectedAt, language),
          { label: t('reconciliation.fields.firstRunId'), value: exception.runId },
          { label: t('reconciliation.fields.lastRunId'), value: exception.lastRunId },
        ]}
      />
    </Section>
  );
}

function MismatchEvidence({ exception }: { readonly exception: ReconciliationExceptionDetail }) {
  const { t } = useTranslation();
  const evidence = reconciliationEvidence(exception);
  const money = (value: string | null, currency: string | null) =>
    value === null || currency === null
      ? t('common.notAvailable')
      : formatExactMoney(value, currency);
  return (
    <Section title={t('reconciliation.sections.mismatchEvidence')}>
      <MetadataList
        items={[
          {
            label: t('reconciliation.fields.sourceTotal'),
            value: money(evidence.sourceTotal, evidence.sourceCurrency),
          },
          {
            label: t('reconciliation.fields.internalTotal'),
            value: money(evidence.internalTotal, evidence.internalCurrency),
          },
          {
            label: t('reconciliation.fields.difference'),
            value: money(evidence.difference, evidence.differenceCurrency),
          },
          {
            label: t('reconciliation.fields.currencyMismatch'),
            value:
              evidence.sourceCurrency === null || evidence.internalCurrency === null
                ? t('common.notAvailable')
                : `${evidence.sourceCurrency} → ${evidence.internalCurrency}`,
          },
        ]}
      />
      <EvidenceList
        label={t('reconciliation.fields.duplicateIds')}
        values={evidence.duplicateIds}
      />
      <EvidenceList
        label={t('reconciliation.fields.missingReceipts')}
        values={evidence.missingReceipts}
      />
      <EvidenceList
        label={t('reconciliation.fields.staleReservations')}
        values={evidence.staleReservations}
      />
      <EvidenceList label={t('reconciliation.fields.evidence')} values={evidence.evidence} />
    </Section>
  );
}

function EvidenceList({
  label,
  values,
}: {
  readonly label: string;
  readonly values: readonly string[];
}) {
  const { t } = useTranslation();
  return (
    <Box sx={{ mt: 2 }}>
      <Typography component="h3" variant="subtitle2">
        {label}
      </Typography>
      {values.length === 0 ? (
        <Typography color="text.secondary">{t('common.notAvailable')}</Typography>
      ) : (
        <Stack component="ul" sx={{ my: 0.5 }}>
          {values.map((value) => (
            <li key={value}>{value}</li>
          ))}
        </Stack>
      )}
    </Box>
  );
}

function RelatedRecords({ exception }: { readonly exception: ReconciliationExceptionDetail }) {
  const auth = useAuth();
  const { t } = useTranslation();
  const permissions = new Set(auth.status === 'authenticated' ? auth.principal.permissions : []);
  const destination = entityDestination(exception.entityType, exception.entityId, permissions);
  return (
    <Paper
      aria-label={t('reconciliation.sections.relatedRecords')}
      component="section"
      role="region"
      sx={{ p: 2 }}
      variant="outlined"
    >
      <Typography component="h2" variant="h5">
        {t('reconciliation.sections.relatedRecords')}
      </Typography>
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Typography>
          {destination === null ? (
            exception.entityId
          ) : (
            <MuiLink component={Link} to={destination}>
              {exception.entityId}
            </MuiLink>
          )}
        </Typography>
        {exception.partner === null ? null : (
          <Typography>
            {permissions.has('PARTNER_VIEW') ? (
              <MuiLink
                component={Link}
                to={`${paths.partners}/${encodeURIComponent(exception.partner.id)}`}
              >
                {exception.partner.code}
              </MuiLink>
            ) : (
              exception.partner.code
            )}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}

function Resolution({
  exception,
  language,
}: {
  readonly exception: ReconciliationExceptionDetail;
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  if (exception.acknowledgement !== null) {
    return (
      <Section title={t('reconciliation.sections.acknowledgement')}>
        <MetadataList
          items={[
            {
              label: t('reconciliation.fields.acknowledgedBy'),
              value: exception.acknowledgement.acknowledgedBy,
            },
            { label: t('reconciliation.fields.reason'), value: exception.acknowledgement.reason },
            {
              label: t('reconciliation.fields.evidenceReference'),
              value: exception.acknowledgement.evidenceReference,
            },
            {
              label: t('reconciliation.fields.evidenceFingerprint'),
              value: exception.acknowledgement.evidenceFingerprint,
            },
            timeItem(
              t('reconciliation.fields.acknowledgedAt'),
              exception.acknowledgement.acknowledgedAt,
              language,
            ),
            timeItem(
              t('reconciliation.fields.lastObservedAt'),
              exception.acknowledgement.lastObservedAt,
              language,
            ),
            {
              label: t('reconciliation.fields.observationCount'),
              value: exception.acknowledgement.observationCount,
            },
          ]}
        />
      </Section>
    );
  }
  if (exception.resolution === null)
    return (
      <Section title={t('reconciliation.sections.resolution')}>
        <Typography color="text.secondary">{t('reconciliation.noResolution')}</Typography>
      </Section>
    );
  return (
    <Section title={t('reconciliation.sections.resolution')}>
      <MetadataList
        items={[
          { label: t('reconciliation.fields.method'), value: exception.resolution.method },
          { label: t('reconciliation.fields.resolvedBy'), value: exception.resolution.resolvedBy },
          { label: t('reconciliation.fields.reason'), value: exception.resolution.reason },
          timeItem(
            t('reconciliation.fields.resolvedAt'),
            exception.resolution.resolvedAt,
            language,
          ),
        ]}
      />
    </Section>
  );
}

interface AcknowledgementForm {
  readonly evidenceReference: string;
  readonly reason: string;
  readonly confirmed: boolean;
}

function AcknowledgeDialog({
  exception,
  open,
  onClose,
  onAcknowledged,
}: {
  readonly exception: ReconciliationExceptionDetail;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onAcknowledged: () => void;
}) {
  const repository = useRepository(reconciliationRepositoryToken);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AcknowledgementForm>({
    defaultValues: { evidenceReference: '', reason: '', confirmed: false },
  });
  const submitting = useRef(false);
  const key = useRef(crypto.randomUUID());
  const [error, setError] = useState<{
    readonly message: string;
    readonly requestId: string | null;
  } | null>(null);
  const { t } = useTranslation();
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      await repository.acknowledgeException(
        exception.id,
        { reason: values.reason, evidenceReference: values.evidenceReference },
        key.current,
      );
      key.current = crypto.randomUUID();
      onAcknowledged();
    } catch (caught) {
      setError(safePageError(caught, t('reconciliation.errors.acknowledge')));
    } finally {
      setTimeout(() => {
        submitting.current = false;
      }, 0);
    }
  });
  return (
    <Dialog aria-describedby="acknowledge-impact" onClose={onClose} open={open}>
      <DialogTitle>{t('reconciliation.actions.acknowledgeException')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="acknowledge-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          <Typography id="acknowledge-impact">{t('reconciliation.acknowledgeImpact')}</Typography>
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
          <TextField
            error={errors.evidenceReference !== undefined}
            label={t('reconciliation.fields.evidenceReference')}
            required
            {...register('evidenceReference', {
              required: true,
              maxLength: RESOLUTION_REASON_MAX_LENGTH,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            label={t('reconciliation.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: true,
              maxLength: RESOLUTION_REASON_MAX_LENGTH,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <FormControlLabel
            control={<Checkbox required {...register('confirmed', { required: true })} />}
            label={t('reconciliation.confirmAcknowledgement')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button form="acknowledge-form" type="submit" variant="contained">
          {t('reconciliation.actions.acknowledgeException')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ResolutionForm {
  readonly evidence: string;
  readonly reason: string;
  readonly confirmed: boolean;
}

function ResolveDialog({
  exception,
  open,
  onClose,
  onResolved,
}: {
  readonly exception: ReconciliationExceptionDetail;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onResolved: () => void;
}) {
  const repository = useRepository(reconciliationRepositoryToken);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResolutionForm>({ defaultValues: { evidence: '', reason: '', confirmed: false } });
  const submitting = useRef(false);
  const key = useRef(crypto.randomUUID());
  const [error, setError] = useState<{
    readonly message: string;
    readonly requestId: string | null;
  } | null>(null);
  const { t } = useTranslation();
  const submit = handleSubmit(async (values) => {
    if (submitting.current) return;
    submitting.current = true;
    setError(null);
    try {
      await repository.resolveException(
        exception.id,
        { reason: composeResolutionReason(values.evidence, values.reason) },
        key.current,
      );
      key.current = crypto.randomUUID();
      onResolved();
    } catch (caught) {
      setError(
        caught instanceof RangeError
          ? { message: t('reconciliation.errors.combinedMaximum'), requestId: null }
          : safePageError(caught, t('reconciliation.errors.resolve'), {
              RECONCILIATION_STILL_MISMATCHED: t('reconciliation.errors.stillMismatched'),
            }),
      );
    } finally {
      setTimeout(() => {
        submitting.current = false;
      }, 0);
    }
  });
  return (
    <Dialog aria-describedby="resolve-impact" onClose={onClose} open={open}>
      <DialogTitle>{t('reconciliation.actions.resolveException')}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="resolve-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          <Typography id="resolve-impact">{t('reconciliation.resolveImpact')}</Typography>
          {error === null ? null : (
            <Alert severity="error">
              {error.message}
              {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
            </Alert>
          )}
          <TextField
            error={errors.evidence !== undefined}
            label={t('reconciliation.fields.evidenceReference')}
            required
            {...register('evidence', {
              maxLength: RESOLUTION_REASON_MAX_LENGTH,
              required: true,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <TextField
            error={errors.reason !== undefined}
            label={t('reconciliation.fields.reason')}
            multiline
            required
            {...register('reason', {
              required: true,
              maxLength: RESOLUTION_REASON_MAX_LENGTH,
              validate: (value) => value.trim().length > 0,
            })}
          />
          <FormControlLabel
            control={<Checkbox required {...register('confirmed', { required: true })} />}
            label={t('reconciliation.confirmResolution')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button form="resolve-form" type="submit" variant="contained">
          {t('reconciliation.actions.resolveException')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Section({
  children,
  title,
}: {
  readonly children: React.ReactNode;
  readonly title: string;
}) {
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h5" sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
function timeItem(label: string, value: string, language: TimeLanguage) {
  const result = formatTimestamp(value, 'Asia/Bangkok', language);
  return { label, value: `${result.display} (${result.originalOffset})` };
}
function entityDestination(
  type: string,
  id: string,
  permissions: ReadonlySet<string>,
): string | null {
  if (type === 'TRANSACTION' && permissions.has('TRANSACTION_VIEW'))
    return `${paths.transactions}/${encodeURIComponent(id)}`;
  if ((type === 'WALLET' || type === 'AFFILIATE_WALLET') && permissions.has('WALLET_VIEW'))
    return `${paths.affiliateWallets}/${encodeURIComponent(id)}`;
  if (
    (type === 'WITHDRAWAL' || type === 'AFFILIATE_WITHDRAWAL') &&
    permissions.has('WITHDRAWAL_VIEW')
  )
    return `${paths.affiliateWithdrawals}/${encodeURIComponent(id)}`;
  if (type === 'PARTNER' && permissions.has('PARTNER_VIEW'))
    return `${paths.partners}/${encodeURIComponent(id)}`;
  return null;
}
